import React, { useState, useEffect } from 'react';
import { Calculator, AlertCircle, CheckCircle, TrendingUp } from 'lucide-react';
import api from '../utils/axios.ts';

const LoanCalculator = ({ onApply }) => {
  const [loanTypes, setLoanTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calculation, setCalculation] = useState(null);
  
  const [formData, setFormData] = useState({
    principal: '',
    loan_type_id: '',
    duration: ''
  });

  useEffect(() => {
    fetchLoanTypes();
  }, []);

  const fetchLoanTypes = async () => {
    try {
      const response = await api.get('/loan-type');
      // Handle both array and paginated responses
      const data = response.data.data;
      const loanTypesArray = Array.isArray(data) ? data : (data?.data || []);
      setLoanTypes(loanTypesArray);
    } catch (error) {
      console.error('Error fetching loan types:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear calculation when inputs change
    if (calculation) {
      setCalculation(null);
    }
  };

  const handleCalculate = async (e) => {
    e.preventDefault();
    
    if (!formData.principal || !formData.loan_type_id) {
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/memberloan/calculate', formData);
      setCalculation(response.data.data);
    } catch (error) {
      console.error('Error calculating loan:', error);
      alert('Failed to calculate loan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const selectedLoanType = loanTypes.find(lt => lt.id === parseInt(formData.loan_type_id));

  return (
    <div className="bg-white rounded-lg p-6">
      <form onSubmit={handleCalculate} className="space-y-4">
        {/* Loan Type Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Loan Type
          </label>
          <select
            name="loan_type_id"
            value={formData.loan_type_id}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          >
            <option value="">Select loan type</option>
            {loanTypes.map(type => (
              <option key={type.id} value={type.id}>
                {type.name} ({type.monthly_interest}% interest)
              </option>
            ))}
          </select>
        </div>

        {/* Principal Amount */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Loan Amount (KES)
          </label>
          <input
            type="number"
            name="principal"
            value={formData.principal}
            onChange={handleChange}
            placeholder="Enter amount"
            min="1"
            step="1"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        {/* Duration (optional) */}
        {selectedLoanType && !selectedLoanType.duration && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Duration ({selectedLoanType.duration_unit || 'months'})
            </label>
            <input
              type="number"
              name="duration"
              value={formData.duration}
              onChange={handleChange}
              placeholder={`Enter duration in ${selectedLoanType.duration_unit || 'months'}`}
              min="1"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        )}

        {/* Calculate Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Calculating...
            </>
          ) : (
            <>
              <Calculator size={20} />
              Calculate Loan
            </>
          )}
        </button>
      </form>

      {/* Calculation Results */}
      {calculation && (
        <div className="mt-6 space-y-4">
          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Loan Summary</h3>
            
            {/* Warning for negative disbursement */}
            {calculation.warning && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-start gap-3">
                <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="text-red-800 font-medium">Cannot Process Loan</p>
                  <p className="text-red-700 text-sm mt-1">{calculation.warning}</p>
                </div>
              </div>
            )}

            {/* Loan Details Grid */}
            <div className="grid grid-cols-1 gap-4 mb-4 sm:grid-cols-2">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Principal Amount</p>
                <p className="text-xl font-bold text-blue-600">{formatCurrency(calculation.principal)}</p>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Interest ({calculation.interest_rate}% {calculation.rate_period_label})</p>
                <p className="text-xl font-bold text-green-600">{formatCurrency(calculation.interest)}</p>
              </div>
              
              <div className="bg-orange-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Processing Fees</p>
                <p className="text-xl font-bold text-orange-600">{formatCurrency(calculation.total_fees)}</p>
              </div>
              
              <div className={`p-4 rounded-lg ${calculation.disbursed_amount < 0 ? 'bg-red-50' : 'bg-purple-50'}`}>
                <p className="text-sm text-gray-600">You Receive</p>
                <p className={`text-xl font-bold ${calculation.disbursed_amount < 0 ? 'text-red-600' : 'text-purple-600'}`}>
                  {formatCurrency(calculation.disbursed_amount)}
                </p>
              </div>
            </div>

            {/* Fee Breakdown */}
            {calculation.fee_breakdown && calculation.fee_breakdown.length > 0 && (
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Fee Breakdown:</p>
                <ul className="space-y-1">
                  {calculation.fee_breakdown.map((fee, index) => (
                    <li key={index} className="text-sm text-gray-600 flex justify-between">
                      <span>{fee.name}</span>
                      <span className="font-medium">{formatCurrency(fee.amount)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Repayment Summary */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-blue-100 text-sm">Total Amount to Repay</p>
                  <p className="text-3xl font-bold">{formatCurrency(calculation.total_amount)}</p>
                </div>
                <TrendingUp size={40} className="text-blue-200" />
              </div>
              
              <div className="border-t border-blue-400 pt-4 mt-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-blue-100 text-sm">{calculation.installment_label}</p>
                    <p className="text-2xl font-bold">{formatCurrency(calculation.installment_amount)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-blue-100 text-sm">Duration</p>
                    <p className="text-xl font-bold">{calculation.duration} {calculation.duration_unit}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Apply Button */}
            {!calculation.warning && onApply && (
              <button
                onClick={() => onApply(formData)}
                className="w-full mt-4 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
              >
                <CheckCircle size={20} />
                Apply for This Loan
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoanCalculator;
